import React, { useState, useEffect, useCallback } from 'react';
import { Plus, StickyNote, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { caseNotesService } from '../../services/api';
import NoteCard from './NoteCard';
import NoteEditorModal from './NoteEditorModal';
import ConfirmDialog from '../common/ConfirmDialog';

const CaseNotesSection = ({ caseId, onSuccess, onError, autoOpen, onAutoOpenHandled }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalNotes, setTotalNotes] = useState(0);
  const [pinnedCount, setPinnedCount] = useState(0);

  // Modal states
  const [showEditor, setShowEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Filter states
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Collapse state
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Auto-open editor when triggered from navbar quick action
  useEffect(() => {
    if (autoOpen) {
      setShowEditor(true);
      setIsCollapsed(false);
      if (onAutoOpenHandled) onAutoOpenHandled();
    }
  }, [autoOpen, onAutoOpenHandled]);

  const noteTypes = caseNotesService.getNoteTypes();

  const fetchNotes = useCallback(async () => {
    if (!caseId) return;

    try {
      setLoading(true);
      const params = {};
      if (filterType) {
        params.note_type = filterType;
      }

      const response = await caseNotesService.getNotes(caseId, params);
      setNotes(response.data.notes || []);
      setTotalNotes(response.data.total || 0);
      setPinnedCount(response.data.pinned_count || 0);
    } catch (err) {
      console.error('Error fetching case notes:', err);
      onError?.('Failed to load case notes');
    } finally {
      setLoading(false);
    }
  }, [caseId, filterType, onError]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSaveNote = async (noteData, noteId) => {
    if (noteId) {
      // Update existing note
      await caseNotesService.updateNote(caseId, noteId, noteData);
      onSuccess?.('Note updated successfully');
    } else {
      // Create new note
      await caseNotesService.createNote(caseId, noteData);
      onSuccess?.('Note added successfully');
    }
    fetchNotes();
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowEditor(true);
  };

  const handleDeleteNote = (note) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await caseNotesService.deleteNote(caseId, noteToDelete.id);
      onSuccess?.('Note deleted');
      fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      onError?.(err.response?.data?.detail || 'Failed to delete note');
    } finally {
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await caseNotesService.togglePin(caseId, note.id);
      onSuccess?.(note.is_pinned ? 'Note unpinned' : 'Note pinned');
      fetchNotes();
    } catch (err) {
      console.error('Error toggling pin:', err);
      onError?.(err.response?.data?.detail || 'Failed to update note');
    }
  };

  const handleNewNote = () => {
    setEditingNote(null);
    setShowEditor(true);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center space-x-2 text-xl font-semibold text-gray-900 hover:text-gray-700"
          >
            <StickyNote className="h-5 w-5" />
            <span>Case Notes</span>
            {totalNotes > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({totalNotes}{pinnedCount > 0 && `, ${pinnedCount} pinned`})
              </span>
            )}
            {isCollapsed ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            )}
          </button>
          <div className="flex items-center space-x-2">
            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm border transition-colors ${
                  filterType
                    ? 'border-yellow-500 text-yellow-700 bg-yellow-50'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4" />
                <span>{filterType ? noteTypes.find(t => t.value === filterType)?.label : 'Filter'}</span>
              </button>

              {showFilters && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => {
                      setFilterType('');
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      !filterType ? 'text-yellow-600 font-medium' : 'text-gray-700'
                    }`}
                  >
                    All Notes
                  </button>
                  {noteTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setFilterType(type.value);
                        setShowFilters(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        filterType === type.value ? 'text-yellow-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {type.icon} {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Add Note Button */}
            <button
              onClick={handleNewNote}
              className="bg-green-600 text-white px-3 py-1.5 rounded text-sm flex items-center hover:bg-green-700 transition-colors"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Note
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-6 pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8">
              <StickyNote className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">
                {filterType ? 'No notes match this filter' : 'No notes yet'}
              </p>
              <p className="text-gray-500 text-sm">
                {filterType
                  ? 'Try a different category or clear the filter'
                  : 'Add notes to track important events and observations'}
              </p>
              {!filterType && (
                <button
                  onClick={handleNewNote}
                  className="mt-4 text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                >
                  + Add your first note
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                  onTogglePin={handleTogglePin}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Note Editor Modal */}
      <NoteEditorModal
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingNote(null);
        }}
        onSave={handleSaveNote}
        editingNote={editingNote}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setNoteToDelete(null);
        }}
        onConfirm={confirmDeleteNote}
        title="Delete Note"
        message={`Are you sure you want to delete this note${
          noteToDelete?.title ? ` "${noteToDelete.title}"` : ''
        }? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default CaseNotesSection;
